import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormArray, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FormGroupValidationRules } from 'webcommon/shared';
import { FindControlRepository } from 'webcommon/webapi';
import { FcItemModalContext } from '../../../models/index';
import { CustomValidators } from './custom-validators';
import { FormControlExtended, FormGroupExtended } from './form-extended';

export type FormGroupArray = FormGroup | FormArray | FormGroupExtended;
@Injectable()
export class FcListValidationService {
  private itemContext: FcItemModalContext | null = null;
  private listItem: any = null;
  public validations: FormGroupValidationRules = {};

  constructor(private readonly findControlRepository: FindControlRepository) {}

  static showErrors(control: AbstractControl, isSubmitted: boolean = false): boolean {
    if (control.invalid && control.errors != null) {
      if (control.errors.required != null || control.errors.isUnique != null) {
        return isSubmitted;
      }
      return control.dirty || control.touched;
    }
    return false;
  }

  static getFormValidationErrors(tabsFormGroup: FormGroupArray, isSubmitted: boolean): number {
    let controlErrors = 0;
    const countErrors = (formGroup: FormGroupArray) => {
      Object.keys(formGroup.controls).forEach((key: string) => {
        const formItem = formGroup.get(key);
        if (!formItem) {
          return;
        }

        if ((formItem instanceof FormControlExtended && formItem.showErrors)
        || (formItem instanceof AbstractControl && FcListValidationService.showErrors(formItem, isSubmitted))) {
          controlErrors++;
        }

        if (formItem instanceof FormGroup || formItem instanceof FormArray || formItem instanceof FormGroupExtended) {
         countErrors(formItem as FormGroupArray);
        }
      });
    };
    countErrors(tabsFormGroup);
    return controlErrors;
  }

  public initValidation(
    itemContext: FcItemModalContext,
    defaultValidationRules: FormGroupValidationRules = {},
    listItem: any = null
  ): void {
    this.itemContext = itemContext ?? null;
    this.listItem = listItem ?? null;
    const validation: FormGroupValidationRules = {};

    // Step 1: Add default validation rules
    for (const [columnName, rules] of Object.entries(defaultValidationRules)) {
      validation[columnName] = rules;
    }

    // Step 2: Add validation rules from server (old variant)
    // In the future, when all validation rules from backend will be moved to new property, we need to remove Step 2
    const columns = itemContext?.findControlConfig?.FindControlBehavior?.ColumnsConfig?.Columns ?? [];
    for (const column of columns) {
      if (typeof column.Validation === 'object' && !Array.isArray(column.Validation) && column.Validation !== null) {
        // Add validation rules to existing one
        validation[column.ColumnName] = {
          ...validation[column.ColumnName],
          ...column.Validation,
        };
      }
    }

    // Step 3: Add validation rules rules from server (new variant)
    const newValidationColumns = itemContext?.findControlConfig?.FindControlBehavior?.ValidationConfig?.ColumnsValidation ?? [];
    for (const column of newValidationColumns) {
      if (column != null && typeof column === 'object' && 'ColumnName' in column) {
        // Prepare validation rules object to merge
        const columnName = column.ColumnName;
        const validationRules: any = {
          ...column,
        };
        delete validationRules.ColumnName;

        // Add validation rules to existing one
        validation[columnName] = {
          ...validation[columnName],
          ...validationRules,
        };
      }
    }

    this.validations = validation;
  }

  public getValidators(validationName: string): ValidatorFn[] {
    const rules = this.validations[validationName] ?? {};
    const validators: ValidatorFn[] = [];

    // Default validators
    if (typeof rules.IsRequired === 'boolean' && rules.IsRequired) {
      validators.push(Validators.required);
      validators.push(CustomValidators.notEmptyString);
    }
    if (typeof rules.MaxLength === 'number' && rules.MaxLength > 0) {
      validators.push(Validators.maxLength(rules.MaxLength));
    }
    if (typeof rules.MinLength === 'number' && rules.MinLength > 0) {
      validators.push(Validators.minLength(rules.MinLength));
    }
    if (typeof rules.Max === 'number' && rules.Max > 0) {
      validators.push(Validators.max(rules.Max));
    }
    if (typeof rules.Min === 'number' && rules.Min > 0) {
      validators.push(Validators.min(rules.Min));
    }
    if (typeof rules.RegExp === 'string' && rules.RegExp.length > 0) {
      validators.push(Validators.pattern(rules.RegExp));
    }

    // Custom validators
    if (typeof rules.PhoneNumber === 'boolean' && rules.PhoneNumber) {
      validators.push(CustomValidators.phoneNumber);
    }
    return validators;
  }

  public getAsyncValidators(validationName: string): AsyncValidatorFn[] {
    const rules = this.validations[validationName] ?? {};
    const asyncValidators: AsyncValidatorFn[] = [];

    // Custom validators
    if (typeof rules.IsUnique === 'boolean' && rules.IsUnique && typeof this.itemContext?.findControlId === 'string') {
      asyncValidators.push(
        CustomValidators.isUnique(this.findControlRepository, validationName, this.itemContext.findControlId, this.listItem)
      );
    }
    return asyncValidators;
  }

  public setValidatorsAndValue(control: AbstractControl, validationName: string, value: any = null): void {
    control.setValidators(this.getValidators(validationName));
    control.setAsyncValidators(this.getAsyncValidators(validationName));
    control.setValue(value);
  }
}
